import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import InlineError from "../../../shared/ui/Field/InlineError";
import NumberedPagination from "../../../shared/ui/Pagination/NumberedPagination";
import {
  messageFromError,
  parseDrfFieldErrors,
} from "../../../shared/api/drfErrors";
import { createDealMyFinancePayment } from "../../deals/api/dealPrivateFinance";
import { isRequestAbortError } from "../../deals/api/deals";
import { formatMoney } from "../../deals/lib/dealFormatting";
import { getDealStatusLabel } from "../../deals/lib/dealStatusLabels";
import { fetchMyFinanceHub } from "../api/myFinanceHub";
import {
  getFinanceHubIncomeTypeLabel,
  getFinanceHubPaymentStatusLabel,
  orderedCurrencyEntries,
} from "../lib/financeHubLabels";
import {
  FINANCE_HUB_CURRENCY_FILTERS,
  FINANCE_HUB_FINANCE_STATE_FILTERS,
  FINANCE_HUB_INCOME_TYPE_FILTERS,
  FINANCE_HUB_LIFECYCLE_FILTERS,
  FINANCE_HUB_ORDERING_OPTIONS,
  FINANCE_HUB_PAGE_SIZE,
  FINANCE_HUB_STATUS_CHIPS,
  buildFinanceHubActiveFilterChips,
  buildFinanceHubFetchParams,
  buildFinanceHubSearchParams,
  financeHubHasActiveFilters,
  financeHubSearchNeedsCanonicalization,
  kyivTodayISO,
  readFinanceHubQueryParams,
  totalFinanceHubPages,
} from "../lib/financeHubQuery";

const emptyPaymentDraft = () => ({
  amount: "",
  received_at: kyivTodayISO(),
  note: "",
});

const formatDateUk = (value) => {
  if (!value) return "—";
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("uk-UA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
};

const planLabel = (row) => {
  if (!row?.configured) return "Не налаштовано";
  const typeLabel = getFinanceHubIncomeTypeLabel(row.income_type);
  if (row.income_type === "percent") {
    return `${typeLabel} ${row.income_value || ""}%`.trim();
  }
  return `${typeLabel} ${formatMoney(row.income_value, row.effective_currency)}`.trim();
};

/** Normalize server payment_progress for safe rendering only — no money math. */
export function normalizeServerPaymentProgress(raw) {
  if (raw == null || raw === "") return null;
  const numeric = Number(raw);
  if (!Number.isFinite(numeric)) return null;
  if (numeric < 0) return 0;
  if (numeric > 1) return 1;
  return numeric;
}

function MobileLabel({ children }) {
  return <span className="feature-finance-hub__mobile-label">{children}</span>;
}

/** Persistent money-strip / currency metric label — always visible, including desktop. */
function MetricLabel({ children }) {
  return <span className="feature-finance-hub__metric-label">{children}</span>;
}

/** Persistent deal-status label — visible on desktop and mobile (not MobileLabel). */
function DealStatusLabel({ children }) {
  return <span className="feature-finance-hub__deal-status-label">{children}</span>;
}

const FINANCE_HUB_STATUS_CHIP_ICONS = {
  overdue: "fa-solid fa-triangle-exclamation",
  unpaid: "fa-solid fa-circle-xmark",
  partial: "fa-solid fa-circle-half-stroke",
  paid: "fa-solid fa-circle-check",
  overpaid: "fa-solid fa-circle-plus",
  unconfigured: "fa-solid fa-sliders",
};

function QuickAddPaymentDialog({
  open,
  row,
  onClose,
  onSuccess,
}) {
  const dialogRef = useRef(null);
  const firstFieldRef = useRef(null);
  const submittingRef = useRef(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const [draft, setDraft] = useState(emptyPaymentDraft);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const requestClose = () => {
    if (submittingRef.current) return;
    onCloseRef.current();
  };

  useEffect(() => {
    if (!open) return undefined;
    setDraft(emptyPaymentDraft());
    setFormError("");
    setFieldErrors({});
    setSubmitting(false);
    submittingRef.current = false;

    const previousOverflow = document.body.style.overflow;
    const csection = document.getElementById("Csection");
    const previousCsection = csection ? csection.style.overflow : "";
    document.body.style.overflow = "hidden";
    if (csection) csection.style.overflow = "hidden";

    const focusTimer = window.setTimeout(() => {
      if (firstFieldRef.current) firstFieldRef.current.focus();
    }, 0);

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        if (submittingRef.current) return;
        onCloseRef.current();
        return;
      }
      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = dialogRef.current.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      if (csection) csection.style.overflow = previousCsection;
      // Focus restoration is owned by the screen (ordinary vs success/refetch).
    };
  }, [open, row?.deal_id]);

  if (!open || !row) return null;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setFormError("");
    setFieldErrors({});
    try {
      await createDealMyFinancePayment(row.deal_id, {
        amount: draft.amount,
        received_at: draft.received_at,
        note: draft.note,
      });
      onSuccess(row.deal_id);
    } catch (error) {
      const parsed = parseDrfFieldErrors(error, [
        "amount",
        "received_at",
        "note",
        "finance",
      ]);
      setFieldErrors(parsed);
      setFormError(parsed._form || messageFromError(error) || "Не вдалося додати платіж.");
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const remaining = row.display_remaining;
  const currency = row.effective_currency || "";

  return (
    <div
      className="feature-finance-hub__dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="finance-hub-quick-add-title"
      data-testid="finance-hub-quick-add-dialog"
      data-modal-layer="application"
      data-submitting={submitting ? "true" : "false"}
      ref={dialogRef}
    >
      <div
        className="feature-finance-hub__dialog-backdrop"
        aria-hidden="true"
        onClick={requestClose}
      />
      <div className="feature-finance-hub__dialog-panel">
        <div className="feature-finance-hub__dialog-header">
          <h2 id="finance-hub-quick-add-title">Додати платіж</h2>
          <button
            type="button"
            className="action-btn feature-finance-hub__dialog-close"
            onClick={requestClose}
            aria-label="Закрити"
            disabled={submitting}
          >
            ×
          </button>
        </div>
        <div className="feature-finance-hub__dialog-context">
          <p>
            <strong>
              {row.property_title_snapshot || `Угода №${row.deal_id}`}
            </strong>
          </p>
          <p>Валюта: {currency || "—"}</p>
          <p>Очікується: {formatMoney(row.expected_amount, currency)}</p>
          <p>Отримано: {formatMoney(row.received_total, currency)}</p>
          <p>Залишилось: {formatMoney(remaining, currency)}</p>
        </div>
        <form onSubmit={handleSubmit} noValidate>
          <label className="feature-finance-hub__field" htmlFor="finance-hub-amount">
            <span>Сума</span>
            <input
              id="finance-hub-amount"
              ref={firstFieldRef}
              type="text"
              inputMode="decimal"
              value={draft.amount}
              onChange={(event) =>
                setDraft((prev) => ({ ...prev, amount: event.target.value }))
              }
              aria-invalid={Boolean(fieldErrors.amount)}
              required
              disabled={submitting}
            />
            {fieldErrors.amount ? <InlineError>{fieldErrors.amount}</InlineError> : null}
          </label>
          <label className="feature-finance-hub__field" htmlFor="finance-hub-received-at">
            <span>Дата отримання</span>
            <input
              id="finance-hub-received-at"
              type="date"
              value={draft.received_at}
              onChange={(event) =>
                setDraft((prev) => ({ ...prev, received_at: event.target.value }))
              }
              aria-invalid={Boolean(fieldErrors.received_at)}
              required
              disabled={submitting}
            />
            {fieldErrors.received_at ? (
              <InlineError>{fieldErrors.received_at}</InlineError>
            ) : null}
          </label>
          <label className="feature-finance-hub__field" htmlFor="finance-hub-note">
            <span>Нотатка (необовʼязково)</span>
            <textarea
              id="finance-hub-note"
              rows={2}
              value={draft.note}
              onChange={(event) =>
                setDraft((prev) => ({ ...prev, note: event.target.value }))
              }
              aria-invalid={Boolean(fieldErrors.note)}
              disabled={submitting}
            />
            {fieldErrors.note ? <InlineError>{fieldErrors.note}</InlineError> : null}
          </label>
          {formError ? (
            <p className="feature-finance-hub__form-error" role="alert">
              {formError}
            </p>
          ) : null}
          <div className="feature-finance-hub__dialog-actions">
            <button
              type="submit"
              className="action-btn action-btn--primary"
              disabled={submitting}
            >
              {submitting ? "Збереження…" : "Додати платіж"}
            </button>
            <button
              type="button"
              className="action-btn"
              onClick={requestClose}
              disabled={submitting}
            >
              Скасувати
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function FinanceHubRowActions({ row, onQuickAdd }) {
  return (
    <div className="feature-finance-hub__row-actions">
      {row.configured ? (
        <button
          type="button"
          className="action-btn action-btn--primary feature-finance-hub__action"
          data-finance-hub-quick-add={row.deal_id}
          data-testid={`finance-hub-quick-add-${row.deal_id}`}
          onClick={(event) => onQuickAdd(row, event.currentTarget)}
        >
          Додати платіж
        </button>
      ) : (
        <Link
          className="action-btn action-btn--primary feature-finance-hub__action"
          to={`/deals/${row.deal_id}#my-finance`}
        >
          Налаштувати дохід
        </Link>
      )}
      {/* Secondary action: intentionally no action-btn--primary */}
      <Link
        className="action-btn feature-finance-hub__action"
        to={`/deals/${row.deal_id}#my-finance`}
      >
        Відкрити угоду
      </Link>
    </div>
  );
}

function resolveRecordFinanceState(row) {
  if (row?.is_overdue) return "overdue";
  return row?.payment_status || "unset";
}

function FinanceHubRecord({ row, onQuickAdd }) {
  const currency = row.effective_currency || "";
  const statusLabel = getFinanceHubPaymentStatusLabel(row.payment_status, {
    isOverdue: row.is_overdue,
  });
  const progress = normalizeServerPaymentProgress(row.payment_progress);
  const showOverpaid = Number(row.overpaid_amount) > 0;
  const remainingLabel = showOverpaid
    ? formatMoney(row.overpaid_amount, currency)
    : formatMoney(row.display_remaining, currency);
  const remainingTitle = showOverpaid ? "Переплата" : "Залишилось";
  const dealKind = row.is_internal ? "internal" : "external";
  const dealKindLabel = row.is_internal ? "Внутрішня" : "Зовнішня";
  const financeState = resolveRecordFinanceState(row);
  const planState = row.configured ? "configured" : "unconfigured";
  const propertyTitle =
    row.property_title_snapshot ||
    (row.property_id ? `Об’єкт #${row.property_id}` : "—");

  return (
    <article
      className="feature-finance-hub__record"
      data-deal-status={row.deal_status || ""}
      data-deal-kind={dealKind}
      data-configured={row.configured ? "true" : "false"}
      data-finance-state={financeState}
      data-overdue={row.is_overdue ? "true" : "false"}
      data-testid={`finance-hub-row-${row.deal_id}`}
    >
      <div className="feature-finance-hub__record-main">
        <div className="feature-finance-hub__zone feature-finance-hub__zone--identity">
          <div className="feature-finance-hub__cell feature-finance-hub__cell--property">
            <MobileLabel>Об’єкт / № угоди</MobileLabel>
            <p className="feature-finance-hub__title">
              {row.property_id ? (
                <Link
                  className="feature-finance-hub__title-link"
                  to={`/realty/${row.property_id}`}
                >
                  {propertyTitle}
                </Link>
              ) : (
                <span>{propertyTitle}</span>
              )}
            </p>
            <span className="feature-finance-hub__muted">№{row.deal_id}</span>
          </div>

          <div className="feature-finance-hub__cell feature-finance-hub__cell--client">
            <MobileLabel>Клієнт</MobileLabel>
            {row.client_id ? (
              <Link
                className="feature-finance-hub__meta-link"
                to={`/clients/${row.client_id}`}
              >
                {row.client_name_snapshot || "—"}
              </Link>
            ) : (
              <span className="feature-finance-hub__muted">
                {row.client_name_snapshot || "—"}
              </span>
            )}
          </div>

          <div className="feature-finance-hub__cell feature-finance-hub__meta">
            <DealStatusLabel>Статус угоди</DealStatusLabel>
            <div className="feature-finance-hub__meta-badges">
              <span
                className="feature-finance-hub__badge feature-finance-hub__badge--lifecycle"
                data-status={row.deal_status || ""}
              >
                {getDealStatusLabel(row.deal_status)}
              </span>
              <span
                className="feature-finance-hub__badge feature-finance-hub__badge--kind"
                data-kind={dealKind}
                data-testid={`finance-hub-kind-${row.deal_id}`}
              >
                {dealKindLabel}
              </span>
            </div>
          </div>
        </div>

        <div className="feature-finance-hub__zone feature-finance-hub__zone--finance">
          <div className="feature-finance-hub__cell">
            <MobileLabel>План доходу</MobileLabel>
            <span
              className="feature-finance-hub__plan feature-finance-hub__badge feature-finance-hub__badge--plan"
              data-plan={planState}
            >
              {planLabel(row)}
            </span>
          </div>

          <div
            className="feature-finance-hub__metrics"
            data-testid={`finance-hub-metrics-${row.deal_id}`}
          >
            <div className="feature-finance-hub__metric feature-finance-hub__cell--numeric">
              <MetricLabel>Очікується</MetricLabel>
              <span className="feature-finance-hub__metric-value">
                {formatMoney(row.expected_amount, currency)}
              </span>
            </div>
            <div className="feature-finance-hub__metric feature-finance-hub__cell--numeric">
              <MetricLabel>Отримано</MetricLabel>
              <span className="feature-finance-hub__metric-value">
                {formatMoney(row.received_total, currency)}
              </span>
            </div>
            <div
              className={`feature-finance-hub__metric feature-finance-hub__cell--numeric feature-finance-hub__metric--${showOverpaid ? "overpaid" : "remaining"}`}
            >
              <MetricLabel>{remainingTitle}</MetricLabel>
              <span className="feature-finance-hub__metric-value">
                {remainingLabel}
              </span>
            </div>
          </div>

          <div className="feature-finance-hub__cell feature-finance-hub__finance-status">
            <div className="feature-finance-hub__date-row">
              <MobileLabel>Очікувана дата</MobileLabel>
              <time dateTime={row.expected_payment_date || undefined}>
                {formatDateUk(row.expected_payment_date)}
              </time>
              {row.is_overdue ? (
                <span className="feature-finance-hub__overdue-callout">
                  Прострочено
                </span>
              ) : null}
            </div>

            <MobileLabel>Фінансовий статус</MobileLabel>
            <span
              className="feature-finance-hub__badge feature-finance-hub__badge--status"
              data-status={financeState}
            >
              <i className="fa-solid fa-circle feature-finance-hub__status-icon" aria-hidden="true" />
              {statusLabel}
            </span>
            {progress != null ? (
              <div
                className="feature-finance-hub__progress"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(progress * 100)}
                aria-label={`Прогрес оплати ${Math.round(progress * 100)}%`}
                data-testid={`finance-hub-progress-${row.deal_id}`}
              >
                <span style={{ width: `${Math.round(progress * 100)}%` }} />
              </div>
            ) : null}
            {row.last_payment_date || row.last_payment_amount ? (
              <p
                className="feature-finance-hub__last-payment"
                data-testid={`finance-hub-last-payment-${row.deal_id}`}
              >
                <MobileLabel>Останній платіж</MobileLabel>
                <span>
                  {formatDateUk(row.last_payment_date)}
                  {" · "}
                  {formatMoney(row.last_payment_amount, currency)}
                </span>
              </p>
            ) : null}
          </div>
        </div>

        <div className="feature-finance-hub__zone feature-finance-hub__zone--actions">
          <MobileLabel>Дії</MobileLabel>
          <FinanceHubRowActions row={row} onQuickAdd={onQuickAdd} />
        </div>
      </div>
    </article>
  );
}

function FinanceHubScreenContent() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = readFinanceHubQueryParams(searchParams);
  const hasFilters = financeHubHasActiveFilters(query);

  const [queryDraft, setQueryDraft] = useState(query.q);
  const [loadState, setLoadState] = useState("loading");
  const [loadError, setLoadError] = useState(null);
  const [refreshError, setRefreshError] = useState(null);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ currencies: {}, statuses: {} });
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
    page: 1,
    total_pages: 1,
  });
  const [retryToken, setRetryToken] = useState(0);
  const [filtersPanelOpen, setFiltersPanelOpen] = useState(false);
  const [isNarrowViewport, setIsNarrowViewport] = useState(false);
  const [quickAddRow, setQuickAddRow] = useState(null);
  const [announce, setAnnounce] = useState("");
  const fetchInFlightRef = useRef(false);
  const appliedQRef = useRef(query.q);
  const restoreFocusRef = useRef(null);
  const successFocusDealIdRef = useRef(null);
  const focusFallbackRef = useRef(null);
  const hasLoadedOnceRef = useRef(false);
  const prevLoadStateRef = useRef(loadState);

  const focusFallback = () => {
    const fallback = focusFallbackRef.current;
    if (fallback && typeof fallback.focus === "function" && fallback.isConnected) {
      fallback.focus();
    }
  };

  const restoreOrdinaryFocus = () => {
    const trigger = restoreFocusRef.current;
    restoreFocusRef.current = null;
    window.setTimeout(() => {
      if (trigger && trigger.isConnected && typeof trigger.focus === "function") {
        try {
          trigger.focus();
          return;
        } catch (_err) {
          // Fall through to page fallback.
        }
      }
      focusFallback();
    }, 0);
  };

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }
    const media = window.matchMedia("(max-width: 767px)");
    const sync = () => setIsNarrowViewport(Boolean(media.matches));
    sync();
    if (typeof media.addEventListener === "function") {
      media.addEventListener("change", sync);
      return () => media.removeEventListener("change", sync);
    }
    media.addListener(sync);
    return () => media.removeListener(sync);
  }, []);

  useEffect(() => {
    if (appliedQRef.current !== query.q) {
      appliedQRef.current = query.q;
      setQueryDraft(query.q);
    }
  }, [query.q]);

  // Automatic canonicalization only — replace, no history entry.
  useEffect(() => {
    if (!financeHubSearchNeedsCanonicalization(searchParams)) return;
    setSearchParams(
      buildFinanceHubSearchParams(readFinanceHubQueryParams(searchParams)),
      { replace: true },
    );
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const controller = new AbortController();
    fetchInFlightRef.current = true;

    async function loadPage() {
      setLoadState((prev) => (prev === "loaded" || prev === "refreshing" ? "refreshing" : "loading"));
      setLoadError(null);
      if (hasLoadedOnceRef.current) {
        setRefreshError(null);
      }
      try {
        const payload = await fetchMyFinanceHub(
          buildFinanceHubFetchParams(query),
          { signal: controller.signal },
        );
        if (controller.signal.aborted) return;
        const nextRows = Array.isArray(payload.results) ? payload.results : [];
        const responsePage = Number(payload.page) > 0 ? Number(payload.page) : 1;
        setRows(nextRows);
        setSummary(payload.summary || { currencies: {}, statuses: {} });
        setPagination({
          count: payload.count || 0,
          next: payload.next || null,
          previous: payload.previous || null,
          page: responsePage,
          total_pages: payload.total_pages || 1,
        });
        hasLoadedOnceRef.current = true;
        setLoadState("loaded");
        setRefreshError(null);

        // Server-driven page repair — replace only, preserve filters/search/order.
        if (responsePage !== query.page) {
          setSearchParams(
            buildFinanceHubSearchParams({
              ...query,
              page: responsePage,
            }),
            { replace: true },
          );
        }
      } catch (error) {
        if (controller.signal.aborted || isRequestAbortError(error)) return;
        const message = messageFromError(error);
        if (hasLoadedOnceRef.current) {
          // Non-destructive: keep mounted rows during failed background refresh.
          setRefreshError(message || "Не вдалося оновити фінанси.");
          setLoadState("loaded");
        } else {
          setLoadError(message);
          setLoadState("error");
        }
      } finally {
        if (!controller.signal.aborted) {
          fetchInFlightRef.current = false;
        }
      }
    }

    loadPage();
    return () => {
      controller.abort();
      fetchInFlightRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- canonical query fields + retry
  }, [
    query.page,
    query.q,
    query.lifecycle,
    query.finance_state,
    query.income_type,
    query.currency,
    query.expected_from,
    query.expected_to,
    query.ordering,
    retryToken,
  ]);

  // After successful quick-add refetch settles, restore focus to same Deal trigger or fallback.
  useEffect(() => {
    const previous = prevLoadStateRef.current;
    prevLoadStateRef.current = loadState;
    // Only act on a settled transition into loaded (initial or refresh), never while
    // loadState is still the previous "loaded" snapshot before refreshing flips.
    if (loadState !== "loaded") return undefined;
    if (previous !== "refreshing" && previous !== "loading") return undefined;
    const dealId = successFocusDealIdRef.current;
    if (dealId == null) return undefined;
    successFocusDealIdRef.current = null;
    const timer = window.setTimeout(() => {
      const trigger = document.querySelector(
        `[data-finance-hub-quick-add="${dealId}"]`,
      );
      if (trigger && trigger.isConnected && typeof trigger.focus === "function") {
        try {
          trigger.focus();
          return;
        } catch (_err) {
          // Fall through.
        }
      }
      focusFallback();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [loadState, rows]);

  // User-driven transitions push history (Back/Forward restore Hub state).
  const pushQuery = (patch) => {
    setSearchParams(
      buildFinanceHubSearchParams({
        ...query,
        ...patch,
      }),
    );
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    pushQuery({ q: queryDraft, page: 1 });
  };

  const handleReset = () => {
    setQueryDraft("");
    setSearchParams(new URLSearchParams());
  };

  const handleChipClick = (value) => {
    pushQuery({
      finance_state: query.finance_state === value ? "" : value,
      page: 1,
    });
  };

  const handleQuickAdd = (row, triggerEl) => {
    restoreFocusRef.current = triggerEl || null;
    successFocusDealIdRef.current = null;
    setQuickAddRow(row);
  };

  const handleQuickAddClose = () => {
    setQuickAddRow(null);
    restoreOrdinaryFocus();
  };

  const handleQuickAddSuccess = (dealId) => {
    successFocusDealIdRef.current = dealId;
    restoreFocusRef.current = null;
    setQuickAddRow(null);
    setAnnounce("Платіж додано");
    setRetryToken((value) => value + 1);
  };

  const currencyEntries = orderedCurrencyEntries(summary.currencies);
  const statuses = summary.statuses || {};
  const totalPages = Math.max(
    1,
    pagination.total_pages || totalFinanceHubPages(pagination.count, FINANCE_HUB_PAGE_SIZE),
  );
  const isRefreshing = loadState === "refreshing";
  const showInitialSkeleton = loadState === "loading" && rows.length === 0;
  const showResults = rows.length > 0 && (loadState === "loaded" || isRefreshing);
  const showPagination = showResults && totalPages > 1;
  const activeFilterCount = [
    query.lifecycle,
    query.finance_state,
    query.income_type,
    query.currency,
    query.expected_from,
    query.expected_to,
  ].filter(Boolean).length;
  const activeFilterChips = buildFinanceHubActiveFilterChips(query);

  const emptyNoDeals = loadState === "loaded" && pagination.count === 0 && !hasFilters && rows.length === 0;
  const emptyFiltered = loadState === "loaded" && pagination.count === 0 && hasFilters && rows.length === 0;
  const showFatalError = loadState === "error" && rows.length === 0;

  return (
    <div
      className="feature-finance-hub"
      data-feature="finance-hub"
      data-testid="finance-hub-screen"
      data-load-state={loadState}
    >
      <section className="clients-overview feature-finance-hub__page-intro" aria-label="Огляд фінансів">
        <header className="clients-header feature-finance-hub__header">
          <div className="clients-header__text feature-finance-hub__header-text">
            <h1
              className="clients-header__title"
              tabIndex={-1}
              ref={focusFallbackRef}
              data-testid="finance-hub-focus-fallback"
            >
              <i className="fa-solid fa-wallet feature-finance-hub__header-icon" aria-hidden="true" />
              {" "}
              Мої фінанси
            </h1>
            <p className="clients-header__sub feature-finance-hub__subtitle">
              Очікувані доходи та отримані платежі за вашими угодами
            </p>
            <span
              className="feature-finance-hub__privacy"
              data-privacy="owner-private"
              data-testid="finance-hub-privacy"
            >
              <i className="fa-solid fa-lock" aria-hidden="true" />
              Видно лише вам
            </span>
          </div>
          <div className="clients-header__actions">
            <Link className="btn-more feature-finance-hub__deals-link" to="/deals">
              <i className="fa-solid fa-handshake" aria-hidden="true" />
              Угоди
            </Link>
          </div>
        </header>
      </section>

      {/* Financial overview: compact currency strip + status chips */}
      <section className="feature-finance-hub__overview" aria-label="Фінансовий огляд">
        {currencyEntries.length === 0 && loadState === "loaded" && pagination.count > 0 ? (
          <div
            className="feature-finance-hub__overview-insight"
            data-testid="finance-hub-overview-insight"
          >
            <p className="feature-finance-hub__overview-insight-title">
              Зведення за валютою зʼявиться після налаштування доходу
            </p>
            <p className="feature-finance-hub__overview-hint">
              Поки немає угод із збереженим планом доходу — відкрийте угоду та
              налаштуйте розділ «Мої фінанси».
            </p>
            <Link className="action-btn" to="/deals">
              Перейти до угод
            </Link>
          </div>
        ) : null}
        {currencyEntries.length > 0 ? (
          <div className="feature-finance-hub__currency-cards">
            {currencyEntries.map(([code, bucket]) => {
              const hasOverpaid = Number(bucket.overpaid) > 0;
              return (
                <article
                  key={code}
                  className="feature-finance-hub__currency-card"
                  data-testid={`finance-hub-currency-${code}`}
                >
                  <h2>{code}</h2>
                  <dl className="feature-finance-hub__currency-metrics">
                    <div className="feature-finance-hub__currency-metric">
                      <dt>Очікується</dt>
                      <dd>{formatMoney(bucket.expected, code)}</dd>
                    </div>
                    <div className="feature-finance-hub__currency-metric">
                      <dt>Отримано</dt>
                      <dd>{formatMoney(bucket.received, code)}</dd>
                    </div>
                    <div
                      className="feature-finance-hub__currency-metric feature-finance-hub__currency-metric--remaining"
                      data-emphasis="remaining"
                    >
                      <dt>Залишилось</dt>
                      <dd>{formatMoney(bucket.remaining, code)}</dd>
                    </div>
                    {hasOverpaid ? (
                      <div
                        className="feature-finance-hub__currency-metric feature-finance-hub__currency-metric--overpaid"
                        data-emphasis="overpaid"
                      >
                        <dt>Переплата</dt>
                        <dd>{formatMoney(bucket.overpaid, code)}</dd>
                      </div>
                    ) : null}
                  </dl>
                </article>
              );
            })}
          </div>
        ) : null}

        {/* Status shortcuts: shared filter-chip grammar */}
        <div
          className="feature-finance-hub__status-chips"
          role="group"
          aria-label="Фільтр за фінансовим статусом"
        >
          {FINANCE_HUB_STATUS_CHIPS.map((chip) => {
            const count = statuses[chip.statusKey] || 0;
            const pressed = query.finance_state === chip.value;
            const zeroCount = count === 0;
            const iconClass = FINANCE_HUB_STATUS_CHIP_ICONS[chip.value];
            return (
              <button
                key={chip.value}
                type="button"
                className={`filter-chip feature-finance-hub__status-chip${pressed ? " filter-chip--active" : ""}`}
                data-finance-state={chip.value}
                data-zero-count={zeroCount ? "true" : "false"}
                aria-pressed={pressed}
                onClick={() => handleChipClick(chip.value)}
              >
                {iconClass ? (
                  <i
                    className={`${iconClass} feature-finance-hub__status-chip-icon`}
                    aria-hidden="true"
                  />
                ) : null}
                <span>{chip.label}</span>
                <span className="feature-finance-hub__chip-count">{count}</span>
              </button>
            );
          })}
        </div>
      </section>

      {/* Canonical toolbar + advanced filters */}
      <div className="clients-workspace__filters feature-finance-hub__filter-workspace">
        <form
          className="clients-toolbar feature-finance-hub__toolbar"
          onSubmit={handleSearchSubmit}
          role="search"
          aria-label="Фільтри фінансів"
        >
          <div className="clients-toolbar__row feature-finance-hub__filter-primary">
            <label className="clients-search feature-finance-hub__search" htmlFor="finance-hub-search">
              <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
              <span className="visually-hidden">Пошук</span>
              <input
                id="finance-hub-search"
                type="search"
                value={queryDraft}
                onChange={(event) => setQueryDraft(event.target.value)}
                placeholder="Об’єкт, клієнт або № угоди"
              />
            </label>

            <label className="feature-finance-hub__field feature-finance-hub__ordering clients-toolbar__sort">
              <span className="visually-hidden">Сортування</span>
              <select
                value={query.ordering}
                aria-label="Сортування"
                onChange={(event) =>
                  pushQuery({ ordering: event.target.value, page: 1 })
                }
              >
                {FINANCE_HUB_ORDERING_OPTIONS.map((option) => (
                  <option key={option.value || "priority"} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <div className="feature-finance-hub__toolbar-actions">
              <button type="submit" className="action-btn">
                Знайти
              </button>
              {isNarrowViewport ? (
                <button
                  type="button"
                  className="action-btn feature-finance-hub__filters-toggle"
                  aria-expanded={filtersPanelOpen}
                  aria-controls="finance-hub-filters-panel"
                  onClick={() => setFiltersPanelOpen((open) => !open)}
                >
                  Фільтри
                  {activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
                </button>
              ) : null}
              <button
                type="button"
                className="action-btn"
                onClick={handleReset}
                disabled={!hasFilters && !query.ordering}
              >
                Скинути фільтри
              </button>
            </div>
          </div>

          {activeFilterChips.length > 0 ? (
            <div
              className="feature-finance-hub__active-filters"
              data-testid="finance-hub-active-filters"
              aria-label="Активні фільтри"
            >
              {activeFilterChips.map((chip) => (
                <button
                  key={chip.key}
                  type="button"
                  className="filter-chip filter-chip--active"
                  onClick={() => {
                    if (chip.key === "q") {
                      setQueryDraft("");
                    }
                    pushQuery(chip.clear);
                  }}
                >
                  {chip.label}
                  <span aria-hidden="true"> ×</span>
                </button>
              ))}
            </div>
          ) : null}

          <div
            id="finance-hub-filters-panel"
            className="clients-toolbar__row--sub feature-finance-hub__filters-panel"
            data-open={(!isNarrowViewport || filtersPanelOpen) ? "true" : "false"}
            data-testid="finance-hub-filters-panel"
          >
            <fieldset className="feature-finance-hub__filter-group">
              <legend className="feature-finance-hub__filter-group-legend">Угода</legend>
              <label className="feature-finance-hub__field">
                <span>Статус угоди</span>
                <select
                  value={query.lifecycle}
                  onChange={(event) =>
                    pushQuery({ lifecycle: event.target.value, page: 1 })
                  }
                >
                  {FINANCE_HUB_LIFECYCLE_FILTERS.map((option) => (
                    <option key={option.value || "default"} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </fieldset>

            <fieldset className="feature-finance-hub__filter-group">
              <legend className="feature-finance-hub__filter-group-legend">Фінанси</legend>
              <label className="feature-finance-hub__field">
                <span>Фінансовий стан</span>
                <select
                  value={query.finance_state}
                  onChange={(event) =>
                    pushQuery({ finance_state: event.target.value, page: 1 })
                  }
                >
                  {FINANCE_HUB_FINANCE_STATE_FILTERS.map((option) => (
                    <option key={option.value || "all"} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="feature-finance-hub__field">
                <span>Тип доходу</span>
                <select
                  value={query.income_type}
                  onChange={(event) =>
                    pushQuery({ income_type: event.target.value, page: 1 })
                  }
                >
                  {FINANCE_HUB_INCOME_TYPE_FILTERS.map((option) => (
                    <option key={option.value || "all"} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="feature-finance-hub__field">
                <span>Валюта</span>
                <select
                  value={query.currency}
                  onChange={(event) =>
                    pushQuery({ currency: event.target.value, page: 1 })
                  }
                >
                  {FINANCE_HUB_CURRENCY_FILTERS.map((option) => (
                    <option key={option.value || "all"} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </fieldset>

            <fieldset className="feature-finance-hub__filter-group">
              <legend className="feature-finance-hub__filter-group-legend">Очікувана оплата</legend>
              <label className="feature-finance-hub__field">
                <span>Дата від</span>
                <input
                  type="date"
                  value={query.expected_from}
                  onChange={(event) =>
                    pushQuery({ expected_from: event.target.value, page: 1 })
                  }
                />
              </label>
              <label className="feature-finance-hub__field">
                <span>Дата до</span>
                <input
                  type="date"
                  value={query.expected_to}
                  onChange={(event) =>
                    pushQuery({ expected_to: event.target.value, page: 1 })
                  }
                />
              </label>
            </fieldset>
          </div>
        </form>
      </div>

      {showInitialSkeleton ? (
        <div
          className="feature-finance-hub__skeleton"
          data-testid="finance-hub-loading"
          aria-busy="true"
          aria-label="Завантаження фінансів"
        >
          <div className="feature-finance-hub__skeleton-row" />
          <div className="feature-finance-hub__skeleton-row" />
          <div className="feature-finance-hub__skeleton-row" />
        </div>
      ) : null}

      {isRefreshing ? (
        <p
          className="feature-finance-hub__refresh-hint"
          data-testid="finance-hub-refreshing"
          aria-live="polite"
        >
          Оновлення…
        </p>
      ) : null}

      {showFatalError ? (
        <div className="feature-finance-hub__state" role="alert">
          <p>{loadError || "Не вдалося завантажити фінанси."}</p>
          <button
            type="button"
            className="action-btn"
            onClick={() => setRetryToken((value) => value + 1)}
          >
            Спробувати ще
          </button>
        </div>
      ) : null}

      {refreshError ? (
        <div
          className="feature-finance-hub__refresh-error"
          role="status"
          data-testid="finance-hub-refresh-error"
        >
          <p>{refreshError}</p>
          <button
            type="button"
            className="action-btn"
            onClick={() => setRetryToken((value) => value + 1)}
          >
            Спробувати ще
          </button>
        </div>
      ) : null}

      {emptyNoDeals ? (
        <div className="feature-finance-hub__state" data-testid="finance-hub-empty-deals">
          <p>
            Фінанси з'являться після участі в угодах як продавець або покупець.
          </p>
          <Link className="action-btn action-btn--primary" to="/deals">
            Перейти до угод
          </Link>
        </div>
      ) : null}

      {emptyFiltered ? (
        <div className="feature-finance-hub__state" data-testid="finance-hub-empty-filters">
          <p>За вибраними фільтрами немає записів.</p>
          <button type="button" className="action-btn" onClick={handleReset}>
            Скинути фільтри
          </button>
        </div>
      ) : null}

      {/* Results workspace: list/card records + pagination */}
      {showResults || showPagination ? (
        <div className="feature-finance-hub__results-workspace">
          {showResults ? (
            <section
              className="feature-finance-hub__results"
              aria-label="Список фінансів"
              aria-busy={isRefreshing ? "true" : "false"}
              data-layout="cards"
              data-testid="finance-hub-results"
            >
              {rows.map((row) => (
                <FinanceHubRecord
                  key={row.deal_id}
                  row={row}
                  onQuickAdd={handleQuickAdd}
                />
              ))}
            </section>
          ) : null}

          {showPagination ? (
            <NumberedPagination
              currentPage={pagination.page || query.page}
              totalPages={totalPages}
              hasPrevious={Boolean(pagination.previous)}
              hasNext={Boolean(pagination.next)}
              onPageChange={(page) => pushQuery({ page })}
              ariaLabel="Сторінки моїх фінансів"
            />
          ) : null}
        </div>
      ) : null}

      <QuickAddPaymentDialog
        open={Boolean(quickAddRow)}
        row={quickAddRow}
        onClose={handleQuickAddClose}
        onSuccess={handleQuickAddSuccess}
      />

      <div className="visually-hidden" aria-live="polite" data-testid="finance-hub-live">
        {announce}
      </div>
    </div>
  );
}

export default FinanceHubScreenContent;
